import React from 'react';
import { X } from 'lucide-react';

/**
 * Country + event-type filter bar for the All Signals view.
 *
 * Options are supplied by the caller and derive from the profile's own
 * data, so no country, industry or event type is hardcoded here. The
 * label map below is presentation only: an unrecognised event type still
 * renders (title-cased) rather than being hidden.
 */

const EVENT_TYPE_LABELS = {
  permit: 'Building Permits',
  funding: 'Funding Decisions',
  procurement: 'Tenders',
  expansion: 'Expansion',
  new_plant: 'New Plant',
  new_line: 'New Production Line',
  acquisition: 'Acquisition',
};

export function eventTypeLabel(type) {
  if (!type) return '';
  return (
    EVENT_TYPE_LABELS[type] ||
    String(type).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

const REGION_NAMES =
  typeof Intl !== 'undefined' && Intl.DisplayNames
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

export function countryLabel(code) {
  if (!code) return '';
  try {
    return REGION_NAMES ? REGION_NAMES.of(code) || code : code;
  } catch {
    return code;
  }
}

const ORIGIN_LABELS = {
  country_pack: 'Country Pack',
  google_news: 'Google RSS',
};

export default function SignalFilterBar({
  filterOptions = { countries: [], eventTypes: [], origins: [] },
  countryFilter = '',
  eventTypeFilter = '',
  minScoreFilter = 0,
  originFilter = '',
  onCountryChange,
  onEventTypeChange,
  onMinScoreChange,
  onOriginChange,
  onClear,
}) {
  const countries = filterOptions.countries || [];
  const eventTypes = filterOptions.eventTypes || [];
  const origins = filterOptions.origins || [];
  const hasActive = Boolean(countryFilter || eventTypeFilter || minScoreFilter > 0 || originFilter);

  if (countries.length === 0 && eventTypes.length === 0 && !hasActive) {
    return null;
  }

  return (
    <div className="mb-4 space-y-3">
      {eventTypes.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500 mr-1">Category</span>
          <button
            type="button"
            onClick={() => onEventTypeChange('')}
            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
              !eventTypeFilter
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            All
          </button>
          {eventTypes.map((et) => (
            <button
              key={et.type}
              type="button"
              onClick={() => onEventTypeChange(eventTypeFilter === et.type ? '' : et.type)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                eventTypeFilter === et.type
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {eventTypeLabel(et.type)}
              <span className="ml-1 opacity-60">{et.count}</span>
            </button>
          ))}
        </div>
      )}

      {/* Origin filter pills */}
      {origins.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500 mr-1">Source</span>
          <button
            type="button"
            onClick={() => onOriginChange && onOriginChange('')}
            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
              !originFilter
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            All
          </button>
          {origins.map((o) => (
            <button
              key={o.origin}
              type="button"
              onClick={() => onOriginChange && onOriginChange(originFilter === o.origin ? '' : o.origin)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                originFilter === o.origin
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {ORIGIN_LABELS[o.origin] || o.origin}
              <span className="ml-1 opacity-60">{o.count}</span>
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        {countries.length > 0 && (
          <select
            value={countryFilter}
            onChange={(e) => onCountryChange(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">All countries</option>
            {countries.map((c) => (
              <option key={c.code} value={c.code}>
                {countryLabel(c.code)} ({c.count})
              </option>
            ))}
          </select>
        )}

        <select
          value={minScoreFilter}
          onChange={(e) => onMinScoreChange(Number(e.target.value))}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
        >
          <option value={0}>Any score</option>
          <option value={50}>Score 50+</option>
          <option value={70}>Score 70+</option>
          <option value={85}>Score 85+</option>
        </select>

        {hasActive && (
          <button
            type="button"
            onClick={onClear}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
            Clear filters
          </button>
        )}
      </div>
    </div>
  );
}
